import { StateCard } from "@/components/patterns";
import { dateLocale } from "@/lib/dateTime";
import { CheckCheck } from "lucide-react";
import { ListEmpty } from "@/components/patterns";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, FileSignature, Plus, RefreshCw, Search } from "lucide-react";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { toQuery } from "@/lib/filters";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { ListPager } from "@/components/ui/list-pager";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { cn, formatINR } from "@/lib/utils";
import { ProposalStatusBadge } from "@/components/proposals/ProposalStatusBadge";
import { UploadedTag } from "@/components/proposals/UploadedTag";
import { NewProposalDialog } from "@/components/proposals/NewProposalDialog";
import { PageHeader, Person, ViewsPanel } from "@/components/patterns";
import type { ProposalListRow, ProposalStatus } from "@/types/proposal";

type Tab = "ALL" | "MINE_TO_REVIEW" | ProposalStatus;

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "ALL", label: "All" },
  { id: "MINE_TO_REVIEW", label: "Needs my review" },
  { id: "DRAFT", label: "Draft" },
  { id: "IN_REVIEW", label: "In review" },
  { id: "APPROVED", label: "Approved" },
  { id: "SENT", label: "Sent" },
  { id: "VIEWED", label: "Viewed" },
  { id: "ACCEPTED", label: "Accepted" },
  { id: "DECLINED", label: "Declined" },
  { id: "EXPIRED", label: "Expired" },
  { id: "SUPERSEDED", label: "Superseded" },
];

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";

export function ProposalsPage() {
  const navigate = useNavigate();
  const { error } = useToast();
  const { hasPermission } = useAuth();
  const canReview = hasPermission("proposal.approve");
  const canCreate = hasPermission("proposal.create");

  const [tab, setTab] = useState<Tab>("ALL");
  const [search, setSearch] = useState("");
  const [reviewCount, setReviewCount] = useState(0);
  const [newOpen, setNewOpen] = useState(false);

  const list = usePagedList<ProposalListRow>({
    deps: [tab, search],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      try {
        const query = toQuery({
          mine_to_review: tab === "MINE_TO_REVIEW" ? "true" : undefined,
          status: tab !== "ALL" && tab !== "MINE_TO_REVIEW" ? tab : undefined,
          search: search.trim(),
          page,
          page_size: 30,
        });
        return unwrapPage<ProposalListRow>(await api.get(`/api/Proposal/List${query}`));
      } catch (err: any) {
        throw err;
      }
    },
  });

  // Counts beside each view in the panel
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    const keys = TABS.filter((t) => t.id !== "MINE_TO_REVIEW" || canReview).map((t) => t.id);
    Promise.all(
      keys.map((k) =>
        api
          .get(`/api/Proposal/List${toQuery({ mine_to_review: k === "MINE_TO_REVIEW" ? "true" : undefined, status: k !== "ALL" && k !== "MINE_TO_REVIEW" ? k : undefined, page: 1, page_size: 1 })}`)
          .then((r) => [k, unwrapPage(r).total] as const)
          .catch(() => [k, 0] as const),
      ),
    ).then((rows) => setCounts(Object.fromEntries(rows)));
  }, [canReview, list.total]);

  const current = TABS.find((t) => t.id === tab);

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Proposals"
        saved={{
          module: "proposals",
          getConfig: () => ({ tab, search }),
          apply: (c) => {
            setTab((TABS.some((t) => t.id === c.tab) ? c.tab : "ALL") as Tab);
            setSearch(c.search || "");
          },
        }}
        subtitle="Solution documents"
        active={tab}
        onSelect={(k) => setTab(k as Tab)}
        groups={[
          {
            items: [
              { key: "ALL", label: "All proposals", count: counts.ALL },
              { key: "MINE_TO_REVIEW", label: "Needs my review", count: counts.MINE_TO_REVIEW, tone: "warning", hidden: !canReview },
            ],
          },
          {
            heading: "Status",
            items: TABS.filter((t) => t.id !== "ALL" && t.id !== "MINE_TO_REVIEW").map((t) => ({ key: t.id, label: t.label, count: counts[t.id] })),
          },
        ]}
      />

      <PageHeader
        title={tab === "ALL" ? "All proposals" : current?.label || "Proposals"}
        summary={list.total}
        actions={
          <>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={list.reload} disabled={list.loading}>
              <RefreshCw className={cn("size-3.5 mr-1.5", list.loading && "animate-spin")} />
              Refresh
            </Button>
            {canCreate && (
              <Button size="sm" onClick={() => setNewOpen(true)}>
                <Plus className="size-4 mr-1.5" />
                New proposal
              </Button>
            )}
          </>
        }
      />

      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by number, title, customer or deal..."
            className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm shadow-xs"
          />
        </div>
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead>Proposal</TableHead>
                <TableHead>Customer / Deal</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead className="text-right">Indicative Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/proposals/${r.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-primary/10 text-accent-foreground flex items-center justify-center shrink-0">
                        <FileSignature className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm font-mono">{r.display_number}<UploadedTag source={r.source} /></div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">{r.title}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.account?.name || "-"}</div>
                    <div className="text-xs text-muted-foreground">{r.deal?.name}</div>
                  </TableCell>
                  <TableCell><Person name={r.owner ? `${r.owner.first_name} ${r.owner.last_name || ""}`.trim() : ""} /></TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.created_on)}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.valid_until)}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatINR(r.estimate_total)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ProposalStatusBadge status={r.proposal_status} />
                      {r.view_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground" title={`Opened ${r.view_count} time(s)`}>
                          <Eye className="size-3" /> {r.view_count}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    {tab === "MINE_TO_REVIEW" ? (
                      <StateCard compact icon={CheckCheck} title="Nothing waiting for your review" body="Proposals that need you will show up here." />
                    ) : (
                      <ListEmpty
                        noun="proposals" error={list.error} onRetry={list.reload}
                        icon={FileSignature}
                        filtered={!!search.trim() || tab !== "ALL"}
                        onClear={() => {
                          setSearch("");
                          setTab("ALL");
                        }}
                        createLabel="+ New proposal"
                        onCreate={canCreate ? () => setNewOpen(true) : undefined}
                        firstUse="Start one from a deal at the Qualification or Proposal stage."
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <NewProposalDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
